import { formatISO } from "date-fns";

import { PostTransitionRequestType } from "@fartherfinance/frontend/api/TransitionWorkflows";
import { AdvisorId } from "@fartherfinance/frontend/api/Types";

import { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";
import { ROOT_TASK_TYPE_MAPPING } from "@src/multiCustodian/pages/Advisor/components/VirtualSubtasks/constants";
import { ComboboxOption } from "@src/sharedComponents/Combobox/Combobox";

interface VirtualTaskPayload {
  title: string;
  description?: string;
  taskStatus: string;
  taskPriority?: string;
  taskType?: string;
  dueDate?: string;
  assigneeId?: string;
  serviceTeamId?: string;
  children?: VirtualTaskPayload[];
}

interface TransitionPayload extends PostTransitionRequestType {
  virtualSubtasks: VirtualTaskPayload[];
}
interface FormData {
  title: string;
  description: string;
  transitionType: EnhancedAutocompleteOption<string>;
  serviceTeam: EnhancedAutocompleteOption<string> | null;
  advisor: ComboboxOption<string>[];
  assignee: { value?: string | undefined } | null;
  dueDate: Date | null;
  custodian: EnhancedAutocompleteOption<string> | null;
  taskStatus: EnhancedAutocompleteOption<string>;
  taskPriority: EnhancedAutocompleteOption<string>;
}

// Recursively map in-memory virtual task to API shape
const mapVirtualTask = (task: any): VirtualTaskPayload => {
  return {
    title: task.title,
    description: task.description || undefined,
    taskStatus: task.taskStatus?.value ?? task.taskStatus,
    taskPriority: task.taskPriority?.value ?? task.taskPriority,
    taskType:
      task.taskType ??
      (task.isRoot
        ? ROOT_TASK_TYPE_MAPPING[task.id]?.value ?? undefined
        : undefined),
    dueDate: task.dueDate
      ? formatISO(new Date(task.dueDate), { representation: "date" })
      : undefined,
    assigneeId: task.assignee?.value ?? undefined,
    serviceTeamId: task.serviceTeam?.value ?? undefined,
    children:
      Array.isArray(task.children) && task.children.length > 0
        ? task.children.map(mapVirtualTask)
        : undefined,
  };
};

export const mapFormDataToPayload = (
  formData: FormData,
  virtualTasks: any[]
): TransitionPayload => {
  // Validate required fields
  if (!formData.assignee?.value) {
    throw new Error("Assignee is required");
  }
  if (!formData.custodian?.value) {
    throw new Error("Custodian is required");
  }
  if (!formData.transitionType?.value) {
    throw new Error("Transition type is required");
  }

  const advisorIds = (formData.advisor ?? [])
    .map((a) => a.value)
    .filter(Boolean) as AdvisorId[];

  if (advisorIds.length === 0) {
    throw new Error("At least one advisor is required");
  }

  return {
    title: formData.title,
    description: formData.description || undefined,
    transitionType: formData.transitionType
      .value as PostTransitionRequestType["transitionType"],
    serviceTeamId: formData.serviceTeam?.value,
    advisor: advisorIds,
    assignee: formData.assignee.value,
    dueDate: formData.dueDate
      ? formatISO(formData.dueDate, { representation: "date" })
      : formatISO(new Date(), { representation: "date" }),
    custodian: formData.custodian
      .value as PostTransitionRequestType["custodian"],
    taskStatus: formData.taskStatus
      .value as PostTransitionRequestType["taskStatus"],
    taskPriority: formData.taskPriority
      .value as PostTransitionRequestType["taskPriority"],
    virtualSubtasks: virtualTasks.map(mapVirtualTask),
  };
};
